import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import FeaturesService from 'ember-feature-flags/services/features';
import SettingsService from 'cio/services/settings';
import AnalyticsService from 'cio/services/analytics';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';
import EnvironmentService from 'cio/services/environment';
import RouterService from '@ember/routing/router-service';
import ObjectTypeModel from 'cio/models/object-type';
import ObjectModel from 'cio/models/object';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import { Filters, isEmptyFiltersQuery } from 'cio/utils/filters';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { queryDecode, queryEncode, safeQueryDecode } from 'cio/helpers/query';
import deserializeFilters from 'cio/utils/conditions/deserialize/filter';
import { ConditionType } from 'cio/utils/condition-types';
import BillingService from 'cio/services/billing';
import PlanModel from 'cio/models/plan';
import { isNone } from '@ember/utils';
import { FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';
import DS from 'ember-data';
import rangeSelectionHelper from 'cio/utils/range-selection-helper';
import ObjectTypesService from 'cio/services/object-types';
import isDestroyed from 'cio/utils/is-destroyed';
import AuthService from 'cio/services/auth';

// Fixed columns: checkbox, name, created
const FIXED_COLUMN_COUNT = 3;
const ITEMS_PER_PAGE = 50;
const HIDDEN_ATTRIBUTES = ['created_at', 'name'];

export default class ObjectsListController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service billing!: BillingService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service settings!: SettingsService;
  @service router!: RouterService;
  @service objectTypes!: ObjectTypesService;

  declare model: ObjectModel[];
  declare objectType: ObjectTypeModel;

  @tracked showDeleteModal = false;
  @tracked showColumnsModal = false;
  @tracked totalRecs!: number;
  @tracked page = 1;
  @tracked allSelected = false;
  @tracked shiftSelectStart: number | null = null;
  @tracked shiftSelectEnd: number | null = null;
  @tracked selectedObjects: ObjectModel[] = [];
  @tracked filters: string | null = null;
  @tracked message: string | null = null;
  @tracked activeSearchBy = 'name';
  @tracked name = '';

  itemsPerPage = ITEMS_PER_PAGE;
  hiddenAttributes = HIDDEN_ATTRIBUTES;

  get isCountUnknown() {
    return this.totalRecs < 0;
  }

  get selectAllDisabled() {
    return !this.objectTypes.canConfigureObjects || this.isCountUnknown || this.model.toArray().length === 0;
  }

  get hasFilters() {
    return !isNone(this.filters) || this.name !== '';
  }

  get isAllSelected() {
    return (this.totalRecs && this.totalRecs === this.selectedObjects.length) || this.allSelected;
  }

  get canDelete() {
    return this.selectedObjects.length > 0;
  }

  get isBulkDeletion() {
    return this.isAllSelected && this.totalRecs > this.selectedObjects.length;
  }

  get allObjectsOnPageSelected() {
    if (this.selectedObjects == null || this.model == null || this.model.length === 0) {
      return false;
    }

    if (this.isAllSelected && this.totalRecs > this.selectedObjects.length) {
      return false;
    }

    return this.selectedObjects.length === this.model.length;
  }

  get allObjectsOnPageIndeterminate() {
    if (this.isAllSelected && this.totalRecs > this.selectedObjects.length) {
      return false;
    }

    return (this.selectedObjects?.length ?? 0) > 0 && !this.allObjectsOnPageSelected;
  }

  get totalColumns() {
    return FIXED_COLUMN_COUNT + this.objectTypeColumns.length;
  }

  get filterConditions() {
    return safeQueryDecode(this.filters ?? '');
  }

  get conditionTypes() {
    let conditionTypes = [ConditionType.ObjectAttribute];

    // TODO: waiting for BE
    // if (this.features.isEnabled('campaign_trigger_ui_new')) {
    //   conditionTypes.push(ConditionType.RelationshipAttribute);
    // }

    return conditionTypes;
  }

  get conditionListButtonLabel() {
    return {
      [ConditionType.ObjectAttribute]: `Filter by ${this.objectType.name.toLowerCase()} attribute`,
      [ConditionType.RelationshipAttribute]: `Filter by relationship attribute`,
    };
  }

  get objectTypeColumns() {
    return this.settings.environmentSettings.objectTypeColumns?.[this.objectType.id] ?? [];
  }

  setupController(objectType: ObjectTypeModel, model: ReturnType<DS.Store['query']>) {
    this.objectType = objectType;
    this.selectedObjects = [];
    this.allSelected = false;
    this.shiftSelectStart = null;
    this.shiftSelectEnd = null;
    this.totalRecs = model.get('meta.pagination.total' as any);
    this.message = model.get('meta.message' as any);
    this.trackViewSearchFilter();
  }

  @action
  confirmDelete() {
    let data: Record<string, any> = { type: 'cio_object_ids' };

    if (this.allSelected && this.filters != null) {
      data.filters = queryDecode(this.filters as string);
    } else {
      let selectedIds = this.selectedObjects.map((p) => p.identifiers[ObjectIdentifierKey.CioObjectId]);
      data.cio_object_ids = selectedIds;
    }

    let promise = this.api.post(`object_types/${this.objectType.id}/objects/delete`, { data });

    promise.then(() => {
      this.send('reloadModel');
    }, ignorePromiseFailure);

    return promise;
  }

  @action
  updateFilters(conditions: SerializedLegacyFilter[] | Filters) {
    let filterString = queryEncode(conditions);

    // Reset the query param to nothing
    if (isEmptyFiltersQuery(filterString)) {
      this.filters = null;
    } else {
      this.filters = filterString;
    }

    this.page = 1;
  }

  @action
  clearFilters() {
    this.filters = null;
  }

  @action
  showColumnsModalAction() {
    this.showColumnsModal = true;
  }

  @action
  hideColumnsModal() {
    this.showColumnsModal = false;
  }

  @action
  setColumns(columns: string[]) {
    let objectTypeColumns = { [this.objectType.id]: columns };
    let promise = this.settings.saveEnvironmentSetting.perform('objectTypeColumns', objectTypeColumns);
    promise.then(() => (this.showColumnsModal = false), ignorePromiseFailure);
    return promise;
  }

  @action
  trackViewSearchFilter() {
    let filters = queryDecode(this.filters ?? queryEncode({ and: [] })) as Filters | SerializedLegacyFilter[];
    let operator = Array.isArray(filters) ? 'and' : Object.keys(filters)[0];
    let filterList = deserializeFilters(filters, false);
    let counts = filterList.reduce(
      (agg, filter) => {
        if (filter.type === ConditionType.ObjectAttribute) {
          agg.object_attributes = agg.object_attributes + 1;
        } else if (filter.type === ConditionType.RelationshipAttribute) {
          agg.relationship_attributes = agg.relationship_attributes + 1;
        }
        return agg;
      },
      { object_attributes: 0, relationship_attributes: 0 },
    );

    let data: Record<string, any> = {
      operator: this.filters == null ? null : operator,
      planName: (this.billing.subscription?.belongsTo('plan').value() as PlanModel | null)?.name ?? '',
      objectsEnabled: true,
      hasObjects: this.model.length > 0,
      filteredByName: this.name !== '',
      nameIncludesWildcard: this.name.includes('*'),
      ...counts,
    };

    this.analytics.track('objects', 'list_viewed_searched_or_filtered', data);
  }

  @action
  objectChecked(
    obj: ObjectModel,
    index: number,
    checked: boolean,
    metadata: { event: FlyMouseEvent<HTMLInputElement> },
  ) {
    let { tempShiftSelectEnd, tempShiftSelectStart, allSelected } = rangeSelectionHelper<ObjectModel>({
      models: this.model,
      singleModel: obj,
      selectedItems: this.selectedObjects,
      index,
      checked,
      totalRecs: this.totalRecs,
      metadata,
      shiftSelectStart: this.shiftSelectStart,
      shiftSelectEnd: this.shiftSelectEnd,
    });

    this.shiftSelectStart = tempShiftSelectStart;
    this.shiftSelectEnd = tempShiftSelectEnd;
    this.allSelected = allSelected;
  }

  @action
  selectAllObjectsOnPage(checked: boolean) {
    if (checked) {
      this.selectedObjects = this.model.slice();
      this.allSelected = this.selectedObjects.length === this.totalRecs;
    } else {
      this.clearSelection();
    }
  }

  @action
  clearSelection() {
    this.selectedObjects = [];
    this.allSelected = false;
  }

  @action
  setAllSelected() {
    this.allSelected = true;
    this.selectedObjects = this.model.slice();
  }

  @action
  activateSearchBy(activeSearchBy: 'name' | 'filter') {
    if (this.activeSearchBy !== activeSearchBy) {
      this.activeSearchBy = activeSearchBy;
      this.filters = null;
      this.name = '';

      if (this.hasFilters) {
        this.page = 1;
      }
    }
  }

  @action
  onSearch(name: string) {
    let isSameSearch = this.name === name;
    this.name = name;
    this.page = 1;

    if (!isDestroyed(this) && isSameSearch) {
      this.send('reloadModel'); // reload list to ensure latest results are shown (considering changes made in the meantime by backfills or on other browser tabs)
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'objects.type.list': ObjectsListController;
  }
}
